<script setup>
import SaveEmailingVariableAction from '@frontJs/components/actions/projectManagement/modules/SaveEmailingVariableAction.vue';
import FileTextIcon from '@iconify-vue/ph/file-text';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import TrashIcon from '@iconify-vue/ph/trash';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import DisplayContentAction from '@js/components/actions/DisplayContentAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import StatusCell from '@js/components/tables/cells/StatusCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { toastDeleted } from '@js/utils/toast';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant, emailing } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    emailing: {
        type: Object,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'type',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Type',
                }),
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.type.label,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'rawValue',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Valeur',
                    width: 0,
                }),
            cell: (props) => {
                switch (props.row.original.type.value) {
                    case 'html':
                    case 'editor':
                        return h(
                            'div',
                            {
                                class: 'relative inline-flex h-full items-center p-4 justify-start',
                            },
                            [
                                h(
                                    DisplayContentAction,
                                    {
                                        title: 'Valeur',
                                        content: props.row.original.value,
                                    },
                                    {
                                        trigger: () =>
                                            h(ActionButton, {
                                                size: 'small',
                                                title: 'Voir la valeur',
                                                type: 'primary',
                                                icon: FileTextIcon,
                                                square: true,
                                            }),
                                    },
                                ),
                            ],
                        );
                    case 'text':
                    case 'date':
                        return h(TextCell, {
                            text: props.row.original.value,
                        });
                    case 'integer':
                        return h(NumberCell, {
                            number: props.row.original.value,
                        });
                    case 'float':
                        return h(NumberCell, {
                            number: props.row.original.value,
                            withDecimals: true,
                        });
                    default:
                        return h(TextCell, {
                            text: props.row.original.rawValue,
                        });
                }
            },
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'isActive',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Est actif',
                }),
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.isActive,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'updatedAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'MÀJ le',
                }),
            cell: (props) =>
                props.row.original.updatedAt
                    ? h(TextCell, {
                          text: props.row.original.updatedAt,
                      })
                    : null,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            emailing
                                ? [
                                      h(
                                          SaveEmailingVariableAction,
                                          {
                                              tenant,
                                              emailing,
                                              emailingVariable:
                                                  props.row.original,
                                              callback: updateData,
                                          },
                                          {
                                              trigger: () =>
                                                  h(ActionButton, {
                                                      size: 'small',
                                                      title: 'Modifier',
                                                      icon: PencilSimpleIcon,
                                                      square: true,
                                                      type: 'primary',
                                                  }),
                                          },
                                      ),
                                      h(
                                          DeleteAction,
                                          {
                                              url: route(
                                                  'front.projectManagement.modules.deleteEmailingVariable',
                                                  {
                                                      tenant: tenant.id,
                                                      emailingVariable:
                                                          props.row.original.id,
                                                  },
                                              ),
                                              callback: () => {
                                                  toastDeleted();

                                                  updateData();
                                              },
                                          },
                                          {
                                              trigger: () =>
                                                  h(ActionButton, {
                                                      type: 'danger',
                                                      size: 'small',
                                                      title: 'Supprimer',
                                                      icon: TrashIcon,
                                                      square: true,
                                                  }),
                                          },
                                      ),
                                  ]
                                : h(ActionButton, {
                                      size: 'small',
                                      title: 'Modifier',
                                      href: route(
                                          'front.projectManagement.modules.emailingVariable',
                                          {
                                              tenant: tenant.id,
                                              emailingVariable:
                                                  props.row.original.id,
                                          },
                                      ),
                                      icon: PencilSimpleIcon,
                                      square: true,
                                  }),
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.modules.postEmailingVariables', {
        tenant: tenant.id,
    }),
    columnPinning: {
        right: ['actions'],
    },
    data: {
        emailingId: emailing ? emailing.id : null,
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <SaveEmailingVariableAction
                v-if="emailing"
                :tenant="tenant"
                :emailing="emailing"
                :callback="updateData"
            >
                <template #trigger>
                    <ActionButton
                        size="small"
                        :left-icon="PlusIcon"
                        type="primary"
                    >
                        Ajouter
                    </ActionButton>
                </template>
            </SaveEmailingVariableAction>

            <ActionButton
                v-else
                size="small"
                :href="
                    route('front.projectManagement.modules.emailingVariable', {
                        tenant: tenant.id,
                    })
                "
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
